import React, { useState } from 'react';
import { Loader, Plus, Link, X } from 'lucide-react';

const REQUIREMENT_KEYS = [
  'AGE_MIN', 'AGE_MAX', 'LANGUAGE_MIN', 'LANGUAGE_ENGLISH_MIN', 'LANGUAGE_GERMAN_MIN',
  'FUNDS_REQUIRED', 'EXPERIENCE_MIN_YEARS', 'QUALIFICATION_REQUIRED', 'INVITATION_REQUIRED',
  'INVESTMENT_MIN', 'INVESTMENT_REQUIRED', 'POINTS_MIN', 'SKILLS_ASSESSMENT_REQUIRED',
  'OCCUPATION_ON_LIST_REQUIRED', 'ENGLISH_MIN_LEVEL', 'REMOTE_EMPLOYMENT_REQUIRED',
  'INCOME_MIN_MONTHLY_USD', 'BANK_STATEMENTS_REQUIRED_MONTHS', 'WORK_ALLOWED', 'BASIC_REQUIREMENT'
];

const coerceValue = (raw) => {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  const num = Number(raw);
  if (!isNaN(num) && raw !== '') return num;
  return raw;
};

const ModalShell = ({ testId, title, onClose, maxWidth, children }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={onClose} data-testid={testId}>
    <div className="card" style={{ width: '100%', maxWidth, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem' }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="var(--text-muted)" /></button>
      </div>
      {children}
    </div>
  </div>
);

export const RuleModal = ({ programId, onSubmit, onClose, actionLoading }) => {
  const [form, setForm] = useState({ requirement_key: '', operator: '>=', requirement_value: '', source_url: '', source_title: '', is_hard_gate: true, notes: '' });

  const submit = async () => {
    const ok = await onSubmit(programId, { ...form, requirement_value: coerceValue(form.requirement_value) });
    if (ok) onClose();
  };

  return (
    <ModalShell testId="add-rule-modal" title="Add Rule" onClose={onClose} maxWidth="540px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Requirement Key *</label>
          <select value={form.requirement_key} onChange={e => setForm({ ...form, requirement_key: e.target.value })} className="form-input">
            <option value="">Select...</option>
            {REQUIREMENT_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Operator *</label>
            <select value={form.operator} onChange={e => setForm({ ...form, operator: e.target.value })} className="form-input">
              {['>=', '<=', '=', 'IN', 'EXISTS', 'BOOL'].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Value *</label>
            <input value={form.requirement_value} onChange={e => setForm({ ...form, requirement_value: e.target.value })} className="form-input" placeholder="e.g. 7, true, 65" />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Source URL *</label>
          <input value={form.source_url} onChange={e => setForm({ ...form, source_url: e.target.value })} className="form-input" placeholder="https://..." />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Source Title *</label>
          <input value={form.source_title} onChange={e => setForm({ ...form, source_title: e.target.value })} className="form-input" placeholder="Official program page" />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Notes</label>
          <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="form-input" placeholder="Additional context" />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', fontSize: '0.9375rem' }}>
          <input type="checkbox" checked={form.is_hard_gate} onChange={e => setForm({ ...form, is_hard_gate: e.target.checked })} />
          Hard Gate (must pass to be eligible)
        </label>
        <button
          onClick={submit}
          disabled={actionLoading || !form.requirement_key || !form.source_url || !form.source_title}
          className="btn btn-primary"
          style={{ marginTop: '0.5rem' }}
          data-testid="submit-rule-btn"
        >
          {actionLoading ? <Loader size={16} className="animate-spin" /> : <Plus size={16} />}
          Add Rule
        </button>
      </div>
    </ModalShell>
  );
};

export const SourceModal = ({ programId, onSubmit, onClose, actionLoading }) => {
  const [form, setForm] = useState({ source_url: '', source_title: '', source_type: 'primary', notes: '' });

  const submit = async () => {
    const ok = await onSubmit(programId, form);
    if (ok) onClose();
  };

  return (
    <ModalShell testId="add-source-modal" title="Add Source" onClose={onClose} maxWidth="480px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Source URL *</label>
          <input value={form.source_url} onChange={e => setForm({ ...form, source_url: e.target.value })} className="form-input" placeholder="https://..." />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Source Title *</label>
          <input value={form.source_title} onChange={e => setForm({ ...form, source_title: e.target.value })} className="form-input" placeholder="e.g. IRCC Official Page" />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Type</label>
          <select value={form.source_type} onChange={e => setForm({ ...form, source_type: e.target.value })} className="form-input">
            <option value="primary">Primary (Official)</option>
            <option value="supplemental">Supplemental</option>
            <option value="official_process">Official Process Detail</option>
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Notes</label>
          <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="form-input" />
        </div>
        <button
          onClick={submit}
          disabled={actionLoading || !form.source_url || !form.source_title}
          className="btn btn-primary"
          style={{ marginTop: '0.5rem' }}
          data-testid="submit-source-btn"
        >
          {actionLoading ? <Loader size={16} className="animate-spin" /> : <Link size={16} />}
          Add Source
        </button>
      </div>
    </ModalShell>
  );
};
